import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";

import { useAppDispatch, useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { ActionTargetType, Session, SessionTypesEnum, ActionEvent, ActionType } from "../../../../../../types/working-model";
import { getStorageItem } from "../../../../../../utils/local-storage";
import { useTracking } from "../../../../../../utils/tracking";
import { hasReachedMilestone } from "../../../../../../utils/utils";
import db from '../../../../../../utils/datastore/indexed-db';
import { Actions, VideoStateContext } from "../../session-stream-provider";
import * as Signals from '../../../../../../utils/event-emitter';
import useTrackPlaying from "../../../../modules/video/use-track-playing";
import { getLogger } from "../../../../../../utils/debug-logger";
import { CorsWorker as Worker } from '../../../../../../connection/cors-worker';
import { HvHostMap } from '../../../../../../connection/helpers';
import { TimeUpdate } from "../types";
import { helperGetDBOndemandWatchtime } from "../../../../../../hooks/agenda.hooks";
import usePrevious from "../../../../../../utils/use-previous";
import { CourseRequirementType } from "types/courses";
import store from "store/main";
import { watchedSecondsLocalTrack } from "store/actions/event/courses";
import { isObject } from "underscore";
const worker = new Worker(new URL('./workers/video-tracking.worker.ts', import.meta.url)).getWorker();
const workerV2 = new Worker(new URL('./workers/video-tracking-v2.worker.ts', import.meta.url)).getWorker();
const logger = getLogger('bl-video:tracking');

workerV2.addEventListener('message', (event: MessageEvent<unknown>) => {
	logger('Worker V2 message', event.data);
});

worker.addEventListener('message', (
	event: MessageEvent<{ session_id: number, watched_seconds: number }>
) => {
	logger(`Worker message:`, event.data);
	if (
		isObject(event.data)
		&& event.data.session_id
		&& event.data.watched_seconds
	) {
		store.dispatch(
			watchedSecondsLocalTrack(event.data.session_id, event.data.watched_seconds)
		);
	}
});

/*

Be advised:

jest does not like the `import.meta.url` use in the worker import above.

You must mock this component in order to test the component that uses it.

*/

interface ITrackingProps {
	isLive?: boolean;
}

export const Tracking: React.FC<ITrackingProps> = ({ isLive }) => {
	const {
		state: {
			playing,
			session,
			event,
			onDemandVideo,
			durationInSeconds,
			initialPlay,
			paused,
			isMobile,
			ended,
			playbackRate,
			currentDynamicVideo
		},
		dispatch
	} = useContext(VideoStateContext);
	const dispatchRedux = useAppDispatch();
	const { language } = useParams<{ language: string }>();
	const playedSecondsRef = useRef<number>(0);
	const playedPercentageRef = useRef<number>(0);
	const milestoneChecker = useRef(hasReachedMilestone());
	const isPlayingRef = useRef(playing);
	const startWatchtimeRef = useRef(0);
	const languageSelection = useRef(getStorageItem(`event.${event?.event}.language`));
	const sleepInterval = useRef<NodeJS.Timeout | null>(null);
	const playDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const componentWillUnmount = useRef(false);
	const unmountRef = useRef<{ timestamp: number | null, isPlaying: boolean }>();
	const lastActionType = useRef<ActionEvent>();
	const workerInitialized = useRef(false);
	const workerInitTimeout = useRef<NodeJS.Timeout | null>(null);
	const playbackRateRef = useRef(playbackRate); // we do not want to re-trigger all tracked actions when this changes
	const isOnDemand = (session as Session).session_type === SessionTypesEnum.onDemand;
	const isBroadcast = (session as Session).session_type === SessionTypesEnum.broadcast;
	const validPasscodeLists = useTypedSelector(event => event.LiveEventReducer.validPasscodeLists);
	const validPasscodeListsRef = useRef(validPasscodeLists); // we do not want to re-trigger all tracked actions when this changes
	const leaderboardIsOn = useTypedSelector(event => event.LiveEventReducer.leaderboardIsOn);
	const blProfileUser = useTypedSelector(event => event.LiveEventReducer.blProfileUser);
	const blProfileUserToken = useTypedSelector(event => event.LiveEventReducer.blProfileUserToken);
	const registrationUuid = useTypedSelector(event => event.LiveEventReducer.registrationId);
	const userSessionUuid = useTypedSelector(event => event.LiveEventReducer.userSession);
	const channel = useTypedSelector(state => state.LiveEventReducer.eventBundle?.channel);
	const eventId = useTypedSelector(state => state.LiveEventReducer.eventBundle?.event);
	const canSendPlayEvents = useTypedSelector(state => !state.LiveEventReducer.loadingLiveStates && state.LiveEventReducer.liveStatesChecked);
	const course = useTypedSelector(state => state.CourseReducer.course);
	const expectCourse = event?.settings?.course_enabled;
	const courseRequirements = course?.requirements;
	const courseUuid = course?.uuid;
	const blProfileId = blProfileUser?.bl_profile;

	// do not set the hook deps on `session` because each time the event is published 
	// session.modifed_at changes which will re-fire all hooks that depend on the base session
	// so get all individual properties we care about individually
	const sessionUuid = session?.uuid;
	const sessionId = session?.session;
	const startTimestamp = session?.timestamp;
	const endTimestamp = session?.end_timestamp;
	const sessionType = session?.session_type;
	const isBroadcastRef = useRef(isBroadcast);
	const startTimestampRef = useRef(startTimestamp);
	const endTimestampRef = useRef(endTimestamp);
	const sessionTypeRef = useRef(sessionType);
	const isLiveRef = useRef(isLive);
	const sessionUuidRef = useRef(sessionUuid);

	const courseRequirement = useMemo(() => {
		return courseRequirements?.find(req => req.type === CourseRequirementType.watched_minute);
	}, [courseRequirements]);

	const courseTrack = !!courseRequirement;
	const requirementId = courseRequirement?.id;

	const isLiveVideo = currentDynamicVideo?.type === 'live_stream';

	useTrackPlaying({ isPlaying: playing, sessionUuid });

	const previousPlaybackRate = usePrevious(playbackRate);

	const { trackEvent } = useTracking({
		target_id_string: (session as Session).session_type,
		target_type: ActionTargetType.Video,
		current_language: language
	});

	const trackEventRef = useRef(trackEvent);

	useEffect(() => {
		startTimestampRef.current = startTimestamp;
		isBroadcastRef.current = isBroadcast;
		endTimestampRef.current = endTimestamp;
		sessionTypeRef.current = sessionType;
		isLiveRef.current = isLive;
		trackEventRef.current = trackEvent;
		sessionUuidRef.current = sessionUuid;
	}, [startTimestamp, isBroadcast, endTimestamp, sessionType, isLive, trackEvent, sessionUuid]);

	const getSecondsPlayed = useRef((timeOfLastKnownStream?: number) => {
		if (startWatchtimeRef.current) {
			return Math.round(((timeOfLastKnownStream || Date.now()) - startWatchtimeRef.current) / 1000);
		}
		return undefined;
	});

	// if session is live, then target_id_string should be marked as broadcast
	// if session has a timestamp and it has passed, then target_id_string should be on-demand
	// if it's actually just an on-demand session, then target_id_string should be marked as on-demand
	// else just return undefined and let the default values handle it
	// Note: isLive uses the actual "has a live stream" status, not the session timestamp status
	const getSessionTargetIdString = useRef(() => {
		if (!sessionTypeRef.current) return;

		if (isLiveRef.current) {
			return SessionTypesEnum.broadcast;
		}

		if (!startTimestampRef.current || !endTimestampRef.current || sessionTypeRef.current === SessionTypesEnum.onDemand) {
			return SessionTypesEnum.onDemand;
		}
		const hasEndSessionTimePassed = endTimestampRef.current < Date.now();
		if (!isLiveRef.current && hasEndSessionTimePassed) {
			return SessionTypesEnum.onDemand;
		}
	});

	const getVideoTimestamp = useRef(() => {
		// if broadcast session, pull relative timestamp from the session start time
		// else pull from played seconds on video player
		if (isBroadcastRef.current) {
			if (startTimestampRef.current && startTimestampRef.current < Date.now()) {
				return (Date.now() - startTimestampRef.current) / 1000;
			}
			return null;
		}
		return playedSecondsRef.current;
	});

	// order of this effect matters - it must be before any other useEffects that reference it
	useEffect(() => {
		return () => {
			logger(`Tracking component unmounted`);

			// player re-mounts immediately on page load if broadcast is live because the player
			// debounce reset triggers, so just cancel any play events triggered on an immediate mount/remount
			if (playDebounceTimeout.current) clearTimeout(playDebounceTimeout.current);
			componentWillUnmount.current = true;

			const secondsPlayed = getSecondsPlayed.current();

			if (secondsPlayed) {
				logger(`Tracking end action`, getVideoTimestamp.current(), secondsPlayed);
				lastActionType.current = ActionEvent.End;
				db.deleteItem('TIME_OF_LAST_STREAM', `${sessionUuidRef.current}-TIME_OF_LAST_STREAM`).catch(e => console.error(e));
				const _targetIdString = getSessionTargetIdString.current();
				const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};
				trackEventRef.current({
					action: ActionEvent.End,
					action_type: ActionType.Passive,
					...target_id_string,
					miscellaneous: JSON.stringify({
						relative_timestamp: getVideoTimestamp.current(),
						seconds_played: secondsPlayed,
						valid_passcode_lists: validPasscodeListsRef.current,
						playback_rate: playbackRateRef.current
					})
				});
			}
		};
	}, []);

	// store these values as refs so we don't have to re-trigger useEffects more than needed
	useEffect(() => {
		isPlayingRef.current = playing;
	}, [playing]);

	useEffect(() => {
		playbackRateRef.current = playbackRate;
	}, [playbackRate]);

	useEffect(() => {
		validPasscodeListsRef.current = validPasscodeLists;
	}, [validPasscodeLists]);

	useEffect(() => {
		unmountRef.current = { timestamp: getVideoTimestamp.current(), isPlaying: playing };
	}, [playing]);

	/*

	Ended handler MUST preceed the play/pause handler

	*/
	useEffect(() => {
		if (ended) {
			logger(`Tracking end action`, getVideoTimestamp.current(), getSecondsPlayed.current());
			lastActionType.current = ActionEvent.End;
			db.deleteItem('TIME_OF_LAST_STREAM', `${sessionUuid}-TIME_OF_LAST_STREAM`).catch(e => console.error(e));

			const _targetIdString = getSessionTargetIdString.current();
			const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};

			trackEvent({
				action: ActionEvent.End,
				action_type: ActionType.Passive,
				...target_id_string,
				miscellaneous: JSON.stringify({
					relative_timestamp: getVideoTimestamp.current(),
					seconds_played: getSecondsPlayed.current(),
					valid_passcode_lists: validPasscodeListsRef.current,
					playback_rate: playbackRateRef.current
				})
			});

			startWatchtimeRef.current = 0;

			worker.postMessage({ type: 'ended' });
			workerV2.postMessage({ type: 'ended' });
		}
	}, [ended, sessionUuid, trackEvent, getSessionTargetIdString]);

	/*

	Play/pause handler MUST be after the ended handler in order

	*/
	useEffect(() => {
		// do not send anything if we don't know whether this is live yet
		// because the live video could trigger a play event before we are certain that this is a live video
		if (!canSendPlayEvents) {
			logger('Skipping update because the live state is currently loading');
			return;
		}
		const _targetIdString = getSessionTargetIdString.current();
		const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};
		if (paused) {
			logger(`Tracking pause action`, getVideoTimestamp.current(), getSecondsPlayed.current());
			trackEvent({
				action: ActionEvent.Pause,
				action_type: ActionType.Active,
				...target_id_string,
				miscellaneous: JSON.stringify({
					relative_timestamp: getVideoTimestamp.current(),
					seconds_played: getSecondsPlayed.current(),
					valid_passcode_lists: validPasscodeListsRef.current,
					playback_rate: playbackRateRef.current
				})
			});

			db.deleteItem('TIME_OF_LAST_STREAM', `${sessionUuid}-TIME_OF_LAST_STREAM`).catch(e => console.error(e));
			startWatchtimeRef.current = 0;

			if (lastActionType.current !== ActionEvent.Play) {
				worker.postMessage({ type: 'stopped' });
				workerV2.postMessage({ type: 'stopped' });
			}

			lastActionType.current = ActionEvent.Play;
		} else {
			// this has a tendency to overfire
			if (playDebounceTimeout.current) clearTimeout(playDebounceTimeout.current);

			playDebounceTimeout.current = setTimeout(() => {
				logger(`Tracking play action`, getVideoTimestamp.current());
				trackEvent({
					action: ActionEvent.Play,
					action_type: ActionType.Active,
					...target_id_string,
					miscellaneous: JSON.stringify({
						relative_timestamp: getVideoTimestamp.current(),
						seconds_played: undefined,
						valid_passcode_lists: validPasscodeListsRef.current,
						playback_rate: playbackRateRef.current
					})
				});

				db.putItem({
					uuid: `${sessionUuid}-TIME_OF_LAST_STREAM`,
					type: 'TIME_OF_LAST_STREAM',
					data: Date.now(),
				}).catch(e => console.error(e));

				startWatchtimeRef.current = Date.now();

				if (lastActionType.current !== ActionEvent.Pause) {
					worker.postMessage({ type: 'playing' });
					workerV2.postMessage({ type: 'playing' });
				}

				lastActionType.current = ActionEvent.Pause;
			}, 60); // wait a few frames before sending the play action because it tends to over-fire
		}
	}, [paused, trackEvent, sessionUuid, canSendPlayEvents]);

	const trackProgress = useCallback(() => {
		if (isOnDemand && milestoneChecker.current(Math.round(playedPercentageRef.current * 100))) {
			const milestone = {
				...onDemandVideo,
				languageSelection,
				playedSeconds: playedSecondsRef.current,
				played: playedPercentageRef.current,
				playback_url: currentDynamicVideo?.playback_url
			};

			logger(`Tracking milestone`, Math.round(milestone.played * 100) + '%');

			const _targetIdString = getSessionTargetIdString.current();
			const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};

			trackEvent({
				playedSeconds: playedSecondsRef.current,
				action: ActionEvent.Milestone,
				action_type: ActionType.Passive,
				...target_id_string,
				miscellaneous: JSON.stringify({
					milestone,
					valid_passcode_lists: validPasscodeListsRef.current,
					playback_rate: playbackRateRef.current
				})
			});
		}

		if (leaderboardIsOn) {
			logger(`Sending ${playedSecondsRef.current} to leaderboard tracking worker`);
			worker.postMessage({ type: 'progress', payload: { second: playedSecondsRef.current } });
		}

		workerV2.postMessage({
			type: 'progress',
			payload: {
				second: playedSecondsRef.current,
				relative_playback_second: getVideoTimestamp.current()
			}
		});

		if (courseTrack && isLiveVideo) {
			worker.postMessage({ type: 'watching' });
		}
	}, [
		isOnDemand,
		leaderboardIsOn,
		onDemandVideo,
		currentDynamicVideo?.playback_url,

		trackEvent,

		// as of right now only live streams are tracked for courses, not on-demand
		courseTrack,
		isLiveVideo,
	]);

	useEffect(() => {
		if (previousPlaybackRate !== playbackRate) {
			trackEvent({
				playedSeconds: playedSecondsRef.current,
				action: ActionEvent.PlaybackSpeedChange,
				action_type: ActionType.Active,
				miscellaneous: JSON.stringify({
					relative_timestamp: getVideoTimestamp.current(),
					seconds_played: getSecondsPlayed.current(),
					valid_passcode_lists: validPasscodeListsRef.current,
					playback_rate: playbackRate
				})
			});
		}
	}, [playbackRate, previousPlaybackRate, trackEvent]);

	useEffect(() => {
		const updateRefs = (progress: TimeUpdate) => {
			playedSecondsRef.current = progress.playedSeconds;
			playedPercentageRef.current = progress.played;
			trackProgress();
		};

		Signals.addEventListener('video-player-progress', updateRefs);

		return () => {
			Signals.removeEventListener('video-player-progress', updateRefs);
		};
	}, [trackProgress]);

	useEffect(() => {
		if (initialPlay) {
			// Initializing this function inside the if statement to avoid redeclaring it on every render
			const getDBOndemandWatchtime = async (sessionId: number, source: string) => {
				const progressData = await helperGetDBOndemandWatchtime(sessionId, source);
				if (progressData?.watchtime) {
					dispatch({
						type: Actions.SeekTo,
						payload: progressData?.watchtime,
					});
				}
			};

			logger(`Tracking initial play action`, getVideoTimestamp.current());

			if (session?.session && onDemandVideo?.source) {
				getDBOndemandWatchtime(session.session, onDemandVideo?.source);
			}

			const targetIdString = getSessionTargetIdString.current();
			const target_id_string = targetIdString ? { target_id_string: targetIdString } : {};
			trackEvent({
				action: ActionEvent.Start,
				action_type: ActionType.Passive,
				...target_id_string,
				miscellaneous: JSON.stringify({
					relative_timestamp: getVideoTimestamp.current(),
					valid_passcode_lists: validPasscodeListsRef.current,
					playback_rate: playbackRateRef.current
				})
			});

			startWatchtimeRef.current = Date.now();

			db.putItem({
				uuid: `${sessionUuid}-TIME_OF_LAST_STREAM`,
				type: 'TIME_OF_LAST_STREAM',
				data: Date.now(),
			}).catch(e => console.error(e));
		}
	}, [dispatch, dispatchRedux, initialPlay, session?.session, sessionUuid, trackEvent, onDemandVideo?.source, getSessionTargetIdString]);

	// when video is ended by user leaving page
	const leaveVideoPage = useCallback(async (userAction: string, timestamp?: number | null, isPlaying?: boolean) => {
		if (isPlaying ?? playing) {
			logger(`Tracking leave video page action`, userAction, timestamp ?? getVideoTimestamp.current(), getSecondsPlayed.current());

			const _targetIdString = getSessionTargetIdString.current();
			const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};

			// track event is more consistent if it is sent first thing in this function
			trackEvent({
				action: ActionEvent.End,
				action_type: ActionType.Passive,
				...target_id_string,
				miscellaneous: JSON.stringify({
					relative_timestamp: timestamp ?? getVideoTimestamp.current(),
					userAction,
					seconds_played: getSecondsPlayed.current(),
					playback_rate: playbackRateRef.current
				})
			});

			startWatchtimeRef.current = 0;

			lastActionType.current = ActionEvent.End;
			if (sessionUuid) {
				try {
					await db.deleteItem('TIME_OF_LAST_STREAM', `${sessionUuid}-TIME_OF_LAST_STREAM`);
				} catch (e) {
					console.error(e);
				}
			}
		}
	}, [playing, trackEvent, sessionUuid]);

	// captures navigation from app sidebar
	useEffect(() => {
		return () => {
			if (componentWillUnmount.current) {
				leaveVideoPage('component unmounted', unmountRef.current?.timestamp, unmountRef.current?.isPlaying);
			}
		};
	}, [leaveVideoPage]);

	// mobile - track start and end events based on visibility of page
	// not all mobile devices unmount or unload when app is hidden
	const handleVisibilityChange = useCallback(async () => {
		const backgrounded = document.visibilityState === 'hidden';

		trackEvent({
			action: backgrounded ? ActionEvent.BackgroundVideo : ActionEvent.ForegroundVideo,
			action_type: ActionType.Active,
			miscellaneous: JSON.stringify({ relative_timestamp: getVideoTimestamp.current(), video_playing: playing }),
		});

		if (isMobile) {
			if (backgrounded) {
				leaveVideoPage('mobile - page visibility hidden');

			} else {
				if (isLive) {
					Signals.broadcastSignal('reset-player');
				}

				if (playing) {
					// if the video is playing when the user returns to the page, track a start event
					startWatchtimeRef.current = Date.now();

					lastActionType.current = ActionEvent.Start;
					if (sessionUuid) {
						try {
							await db.putItem({
								uuid: `${sessionUuid}-TIME_OF_LAST_STREAM`,
								type: 'TIME_OF_LAST_STREAM',
								data: Date.now(),
							});
						} catch (e) {
							console.error(e);
						}
					}

					const _targetIdString = getSessionTargetIdString.current();
					const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};

					trackEvent({
						action: ActionEvent.Start,
						action_type: ActionType.Passive,
						miscellaneous: JSON.stringify({ relative_timestamp: getVideoTimestamp.current(), dev: 'mobile - returning to app/page' }),
						playback_rate: playbackRateRef.current,
						...target_id_string
					});
				}
			}
		}
	}, [isMobile, playing, leaveVideoPage, sessionUuid, trackEvent, isLive]);

	useEffect(() => {
		const leaveVideoPageHandler = () => leaveVideoPage('page unloaded via refresh, navigation, or closing');

		window.addEventListener('beforeunload', leaveVideoPageHandler);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			window.removeEventListener('beforeunload', leaveVideoPageHandler);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [handleVisibilityChange, leaveVideoPage]);

	// This is componentWillUnmount
	// This must be before any useEffects that reference it
	useEffect(() => {
		sleepInterval.current = setInterval(() => {
			Signals.broadcastSignal('get-is-player-idle');
		}, 2000);

		Signals.on('video-player-is-idle', async (idle: boolean | undefined) => {
			if (idle) {
				if (isPlayingRef.current) {
					logger(`Player is idle but state thinks we should be playing - machine woke from sleep, fixing state`, getVideoTimestamp.current(), getSecondsPlayed.current());
					dispatch({ type: Actions.SetPlaying, payload: false });
					worker.postMessage({ type: 'idled' });
					workerV2.postMessage({ type: 'idled' });

					const timeOfLastKnownStream = await db.getItem<number>('TIME_OF_LAST_STREAM', `${sessionUuid}-TIME_OF_LAST_STREAM`);
					if (
						(lastActionType.current === ActionEvent.Start || lastActionType.current === ActionEvent.Play)
						&& timeOfLastKnownStream
					) {
						logger(`Tracking pause action due to wake`, getVideoTimestamp.current(), getSecondsPlayed.current());
						const _targetIdString = getSessionTargetIdString.current();
						const target_id_string = _targetIdString ? { target_id_string: _targetIdString } : {};
						trackEvent({
							action: ActionEvent.Pause,
							action_type: ActionType.Passive,
							...target_id_string,
							miscellaneous: JSON.stringify({
								relative_timestamp: getVideoTimestamp.current(),
								seconds_played: getSecondsPlayed.current(timeOfLastKnownStream),
								valid_passcode_lists: validPasscodeListsRef.current,
								playback_rate: playbackRateRef.current
							})
						});
						lastActionType.current = undefined; // DO NOT REMOVE or we'll get an infinite loop
						// do not delete `${sessionUuid}.TIME_OF_LAST_STREAM` from local storage here or we'll get an infinite loop
					}
				}
			}
		});

		return () => {
			Signals.off('video-player-is-idle');
			if (sleepInterval.current) {
				clearInterval(sleepInterval.current);
			}
		};
	}, [dispatch, sessionUuid, trackEvent, getSessionTargetIdString]);

	useEffect(() => {
		// has enough data to send the tracking messages
		const playback = currentDynamicVideo?.playback_url || onDemandVideo?.playback_url;
		const canTrack = !!(sessionUuid && eventId && blProfileId && channel && blProfileUserToken && playback);

		// if there is a course await the course uuid and requirement and only track while the video is live
		if (expectCourse && !(courseUuid && requirementId && isLive)) {
			return;
		}

		// if this is a broadcast, track. If this is any kind of recording with a known duration from the player, track.
		// otherwise, we do not know whether to track or not.
		const hasEnoughVideoData = isBroadcast || (!isBroadcast && durationInSeconds);

		if (canTrack && hasEnoughVideoData && playing) {
			if (workerInitTimeout.current) {
				clearTimeout(workerInitTimeout.current);
			}

			workerInitTimeout.current = setTimeout(() => {
				if (workerInitialized.current) return;

				const timing = isBroadcast ? {
					isLive: true,
					startTime: startTimestamp,
					endTime: endTimestamp
				} : {
					isLive: false,
					duration: durationInSeconds
				};

				worker.postMessage({
					type: 'init',
					payload: {
						sessionUuid,
						blProfileUserToken,
						action: {
							event_id: eventId,
							bl_profile: blProfileId,
							channel_id: channel,
							session_id: sessionId,
							course_uuid: courseUuid,
							requirement_id: requirementId
						},
						timing,
						trackingOrigin: HvHostMap.eventData,
						is_live: isBroadcast,
						is_course: courseTrack,
						course_uuid: courseUuid,
						requirement_id: requirementId,
						watched_seconds: playedSecondsRef.current
					}
				});

				// new type - should run on every video, not just leaderboard and courses
				workerV2.postMessage({
					type: 'init',
					payload: {
						channel_id: channel,
						event_id: eventId,
						session_id: sessionId,
						bl_profile: blProfileId,
						bl_profile_non_registered: undefined,
						registration_uuid: registrationUuid,
						user_session_uuid: userSessionUuid,
						playback_url: playback,
						playback_type: currentDynamicVideo?.type === "live_stream" ? "live" : "ondemand",
						watched_seconds: playedSecondsRef.current,
						relative_playback_second: getVideoTimestamp.current() ?? 0
					}
				});

				workerInitialized.current = true;
			}, 500);
		}
	}, [
		sessionUuid,
		blProfileId,
		channel,
		eventId,
		sessionId,
		durationInSeconds,
		startTimestamp,
		endTimestamp,
		isBroadcast,
		blProfileUserToken,
		courseTrack,
		courseUuid,
		requirementId,
		playing,
		expectCourse,
		registrationUuid,
		userSessionUuid,
		currentDynamicVideo,
		onDemandVideo,
		isLive
	]);

	return (
		<></>
	);
};

export default Tracking;
